<script>
  import Scrolly from "./Scrolly.svelte";
  import TimeSeries from "./TimeSeries.svelte";
  import Geospatial from "./Geospatial.svelte";
  import Beeswarm from "./Beeswarm.svelte";
  import ColoredText from "./ColoredText.svelte";
  import { fade } from "svelte/transition";
  import { linear } from "svelte/easing";
  import { scaleSequential, interpolateViridis } from "d3";

  let value;
  let scrollY;

  const title = "Reaching for Venus";
  const colorScale = scaleSequential([0, 100], interpolateViridis);

  const steps = [
    "<p>In a historic first, the proportion of women parliamentarians throughout the world just crossed the quarter mark, reaching 25.6% in 2020.</p>",

    "<p>We’re still a ways off from gender parity though—extrapolating from the current trend, we’ll have to wait for another 40 years for women to account for 50% or more parliamentary seats.</p><p>Progress seems steady, albeit painstakingly slow.</p>",

    "<p>As another perspective, perhaps we should not be too obsessed with chasing numbers, but rather genuinely focus on promoting efforts to recognize women who can contribute to their respective nations.</p>",

    "<p>Notably, Rwanda, Cuba and the United Arab Emirates have already achieved gender parity. </p>",

    "<p>Over at Singapore, 2020 was also a momentous year.</p> <p>In the 2020 General Elections, a record number of female candidates participated in the election, with 28 out of 40 candidates elected and making up 29.5% of the available seats. This was an increase from 23.9% (21 seats) after the 2015 polls.</p>",

    "<p>In the section that follows, let’s get close and personal with the candidates and faces behind Singapore’s 2020 General Election.</p>",
  ];
</script>

<svelte:window bind:scrollY />

<svelte:head>
  <title>Reaching for Venus</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link
    href="https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&display=swap"
    rel="stylesheet"
  />
</svelte:head>

<section>
  {#if scrollY === 0}
    <div class="hero" transition:fade={{ duration: 400, easing: linear }}>
      <h1>{title}</h1>
      <h2>
        Exploring Female Representation in Parliaments around the World and
        Singapore
      </h2>
    </div>
  {/if}

  <div class="section-container">
    <div class="steps-container">
      <Scrolly bind:value>
        <!-- {#each steps as text, i}
          <div class="step" class:active={value === i}>
            <div class="step-content">{@html text}</div>
          </div>
        {/each} -->
        <div class="step" class:active={value === 0}>
          <div class="step-content">
            <p>
              In a historic first, the proportion of women parliamentarians
              throughout the world just crossed the quarter mark, reaching <ColoredText
                color={colorScale(25.6)}
                text={"25.6%"}
              />
              in 2020.
            </p>
          </div>
        </div>

        <div class="step" class:active={value === 1}>
          <div class="step-content">
            <p>
              We’re still a ways off from <ColoredText
                color={colorScale(50)}
                text={"gender parity"}
              /> though—extrapolating from the current trend, we’ll have to wait
              for another <span class="bold">40 years</span> for women to account for <ColoredText
                color={colorScale(50)}
                text={"50% or more"}
              /> parliamentary seats.
            </p>
            <p>Progress seems steady, albeit painstakingly slow.</p>
          </div>
        </div>

        <div class="step" class:active={value === 2}>
          <div class="step-content">
            <p>
              As another perspective, perhaps we should not be too obsessed with
              chasing numbers, but rather genuinely focus on promoting efforts
              to recognize women who can contribute to their respective nations.
            </p>
          </div>
        </div>

        <div class="step" class:active={value === 3}>
          <div class="step-content">
            <p>
              Notably, <ColoredText color={colorScale(61.3)} text={"Rwanda"} />, <ColoredText
                color={colorScale(53.2)}
                text={"Cuba"}
              /> and the <ColoredText
                color={colorScale(50)}
                text={"United Arab Emirates"}
              /> have already achieved gender parity.
            </p>
          </div>
        </div>

        <div class="step" class:active={value === 4}>
          <div class="step-content">
            <p>Over at Singapore, 2020 was also a momentous year.</p>
            <p>
              In the 2020 General Elections, a record number of female
              candidates participated in the election, with 28 out of 40
              candidates elected and making up <ColoredText
                color={colorScale(29.5)}
                text={"29.5%"}
              /> of the available seats. This was an increase from <ColoredText
                color={colorScale(23.9)}
                text={"23.9%"}
              /> (21 seats) after the 2015 polls.
            </p>
          </div>
        </div>

        <div class="step" class:active={value === 5}>
          <div class="step-content">
            <p>
              In the following section, let’s get close and personal with the
              candidates and faces behind Singapore’s 2020 General Election.
            </p>
          </div>
        </div>

        <div class="spacer" />
      </Scrolly>
    </div>
    <div class="sticky">
      <!-- <Scatterplot step={value} /> -->
      <TimeSeries step={value} {scrollY} />
    </div>
  </div>

  <div class="chart beeswarm">
    <Beeswarm />
  </div>

  <div class="text-container">
    <p>
      Exploration of notable female candidates...
    </p>
  </div>

  <!-- <div class="footer">
    <h1>Thanks!</h1>
    <h2>
      <a href="https://twitter.com/CL_Rothschild" target="_blank"
        >Questions and Tips</a
      >
    </h2>
  </div> -->

  <div class="chart">
    <Geospatial />
  </div>

  <div class="text-container end">
    <p>
      Exploration of geographical trends in female representation around the world... (missing legend and annotations...)
    </p>
  </div>
</section>

<style>
  /* :global(body) {
    overflow-x: hidden;
    padding: 0;
    line-height: 1.5;
  } */
  .hero {
    font-family: var(--font-serif);
    position: fixed;
    padding-top: 20px;
    width: 100%;
    display: flex;
    place-items: center;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    z-index: 2;
    text-shadow: var(--white-text-shadow);
    pointer-events: none;
  }
  .hero h1 {
    font-weight: 700;
    font-size: calc(64 / 16 * 1rem);
    margin: 0;
  }
  .hero h2 {
    margin-top: 0;
    font-weight: 400;
    width: 70%;
  }
  .footer {
    height: 60vh;
    display: flex;
    place-items: center;
    flex-direction: column;
    justify-content: center;
    text-align: center;
  }
  .spacer {
    height: 40vh;
  }
  .sticky {
    position: sticky;
    top: 0%;
    flex: 1 1 60%;
    width: 60%;
  }
  .section-container {
    /* margin-top: 1em; */
    text-align: center;
    transition: background 100ms;
    display: flex;
  }
  .step {
    height: 80vh;
    display: flex;
    place-items: center;
    justify-content: center;
  }
  .step-content {
    font-size: calc(16 / 16 * 1rem);
    font-family: var(--font-serif);
    background: whitesmoke;
    /* background-color: transparent; */
    color: #ccc;
    border-radius: 5px;
    padding: 0.5rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    transition: background 500ms ease;
    box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.2);
    text-align: left;
    width: 75%;
    margin: auto;
    max-width: 500px;
    pointer-events: auto;
    /* text-shadow: var(--white-text-shadow); */
  }
  .step.active .step-content {
    background: white;
    /* background-color: transparent; */
    color: black;
  }
  .steps-container,
  .sticky {
    height: 100%;
  }
  .steps-container {
    flex: 1 1 40%;
    z-index: 10;
    pointer-events: none;
  }
  .bold {
    font-weight: 700;
  }
  .text-container {
    max-width: 600px;
    margin: 0 auto;
    margin-top: 50px;
    margin-bottom: 50px;
    padding: 0 20px;
    font-family: var(--font-serif);
  }
  .end {
    margin-top: 0px;
    padding-bottom: 100px;
  }

  /* Comment out the following line to always make it 'text-on-top' */
  /* @media screen and (max-width: 768px) {
    .section-container {
      flex-direction: column-reverse;
    }
    .sticky {
      width: 95%;
      margin: auto;
    }
  } */
  .section-container {
    flex-direction: column-reverse;
  }
  .sticky {
    width: 100%;
    max-width: 1280px;
    margin: auto;
  }
  .chart {
    width: 100%;
    max-width: 1280px;
    margin: auto;
  }
  .beeswarm {
    margin-top: 100px;
    max-width: 1020px;
  }
</style>
